import * as React from "react";
import Link from "next/link";
import Image from "next/image";
import { IoMenuOutline, IoClose } from "react-icons/io5";
import { useRouter } from "next/router";
import Button from "components/button/Button";
import useDarkMode from "hooks/useDarkMode";
import { MdLightMode, MdDarkMode } from "react-icons/md";

const pages = [
  { title: "Գլխավոր", value: "/" },
  { title: "Կանխատեսումներ", value: "/predictions" },
  { title: "Խոստումներ", value: "/promises" },
  { title: "Կանխատեսողներ", value: "/predictors" },
  { title: "Ընդհանրական", value: "/surveys" },
  { title: "Մեր մասին", value: "/about-us" },
];

function ResponsiveAppBar({ currentUser, isLoading, signOut }) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const router = useRouter();
  const { theme, toggleTheme, mounted } = useDarkMode();

  const isActive = (value) =>
    router.pathname === value ||
    (value !== "/" && router.pathname.startsWith(value));

  // Close drawer on route change
  React.useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [router.pathname]);

  return (
    <div style={{ fontFamily: "Noto Sans Armenian" }} className="relative z-50">
      {/* ─── Fixed Header ─── */}
      <header className="fixed top-0 inset-x-0 z-50 h-16 bg-white/95 dark:bg-[#03001a]/95 backdrop-blur-md border-b border-slate-100 dark:border-white/[0.06]">
        <div className="max-w-screen-xl mx-auto h-full px-5 md:px-10 flex items-center justify-between gap-4">

          {/* Logo */}
          {!router.pathname.includes("auth") && (
            <Link href="/" className="shrink-0 hover:opacity-80 transition-opacity">
              <img
                src={mounted && theme === "dark" ? "/logo-dark.svg" : "/logo.svg"}
                alt="Prognos"
                className="h-8 w-auto"
              />
            </Link>
          )}

          {/* ─── Desktop Nav ─── */}
          <nav className="hidden xl:flex items-center gap-0.5">
            {pages.map((page) => {
              const active = isActive(page.value);
              return (
                <Link
                  key={page.value}
                  href={page.value}
                  className={`relative px-3.5 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    active
                      ? "text-[#0033FF] bg-[#f0f4ff] dark:bg-[#0033FF]/10"
                      : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-50 dark:hover:bg-white/[0.04]"
                  }`}
                >
                  {page.title}
                </Link>
              );
            })}
          </nav>

          {/* ─── Desktop Actions ─── */}
          <div className="hidden xl:flex items-center gap-1.5">
            {/* Theme toggle */}
            {mounted && (
              <button
                onClick={toggleTheme}
                className="w-9 h-9 flex items-center justify-center rounded-lg text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/[0.06] transition-all"
                aria-label="Toggle theme"
              >
                {theme === "dark" ? (
                  <MdLightMode className="text-[18px]" />
                ) : (
                  <MdDarkMode className="text-[18px]" />
                )}
              </button>
            )}

            {/* Auth buttons */}
            {!router.pathname.includes("auth") && !isLoading && !currentUser && (
              <div className="flex items-center gap-1.5 ml-1">
                <button
                  onClick={() =>
                    router.push({ pathname: "/auth", query: { mode: "registration" } }, undefined, { shallow: true })
                  }
                  className="px-4 py-1.5 text-sm font-semibold text-[#0033FF] hover:bg-[#f0f4ff] dark:hover:bg-[#0033FF]/10 rounded-lg transition-all"
                >
                  Գրանցվել
                </button>
                <button
                  onClick={() =>
                    router.push({ pathname: "/auth", query: { mode: "login" } }, undefined, { shallow: true })
                  }
                  className="px-5 py-1.5 text-sm font-semibold text-white bg-[#0033FF] hover:bg-[#0029d6] rounded-full shadow-sm transition-all"
                >
                  Մուտք
                </button>
              </div>
            )}

            {/* Logged-in user */}
            {!isLoading && currentUser && (
              <div className="flex items-center gap-2 ml-1">
                <button
                  onClick={() => router.push("/profile")}
                  className="w-8 h-8 rounded-full overflow-hidden ring-2 ring-[#0033FF]/30 hover:ring-[#0033FF] transition-all shrink-0"
                >
                  <Image
                    width={32}
                    height={32}
                    src={currentUser?.photoURL || "/defaultuserprofile.webp"}
                    className="object-cover w-full h-full"
                    alt="avatar"
                  />
                </button>
                <button
                  onClick={signOut}
                  className="px-3 py-1.5 text-xs font-semibold text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-all"
                >
                  Դուրս գալ
                </button>
              </div>
            )}
          </div>

          {/* ─── Mobile Right Controls ─── */}
          <div className="xl:hidden flex items-center gap-1 ml-auto">
            {mounted && (
              <button
                onClick={toggleTheme}
                className="w-9 h-9 flex items-center justify-center rounded-lg text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/[0.06] transition-all"
                aria-label="Toggle theme"
              >
                {theme === "dark" ? (
                  <MdLightMode className="text-[18px]" />
                ) : (
                  <MdDarkMode className="text-[18px]" />
                )}
              </button>
            )}
            <button
              onClick={() => setIsMobileMenuOpen((v) => !v)}
              className="w-9 h-9 flex items-center justify-center rounded-lg text-slate-700 dark:text-white hover:bg-slate-100 dark:hover:bg-white/[0.06] transition-all"
              aria-label="Open menu"
            >
              {isMobileMenuOpen ? (
                <IoClose className="text-[22px]" />
              ) : (
                <IoMenuOutline className="text-[22px]" />
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Spacer */}
      <div className="h-16" />

      {/* ─── Mobile Drawer ─── */}
      <div
        className={`xl:hidden fixed inset-0 z-[60] flex flex-col bg-white dark:bg-[#03001a] transition-all duration-300 ${
          isMobileMenuOpen
            ? "opacity-100 pointer-events-auto translate-x-0"
            : "opacity-0 pointer-events-none translate-x-full"
        }`}
      >
        {/* Drawer header */}
        <div className="h-16 px-5 flex items-center justify-between border-b border-slate-100 dark:border-white/[0.06] shrink-0">
          <Link href="/" onClick={() => setIsMobileMenuOpen(false)}>
            <img
              src={mounted && theme === "dark" ? "/logo-dark.svg" : "/logo.svg"}
              alt="Prognos"
              className="h-8 w-auto"
            />
          </Link>
          <button
            onClick={() => setIsMobileMenuOpen(false)}
            className="w-9 h-9 flex items-center justify-center rounded-lg text-slate-700 dark:text-white hover:bg-slate-100 dark:hover:bg-white/[0.06] transition-all"
          >
            <IoClose className="text-[22px]" />
          </button>
        </div>

        {/* Nav links */}
        <div className="flex-1 overflow-y-auto px-4 py-5 flex flex-col gap-1">
          {pages.map((page) => {
            const active = isActive(page.value);
            return (
              <Link
                key={page.value}
                href={page.value}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-4 py-3.5 rounded-xl text-[15px] font-semibold transition-all ${
                  active
                    ? "bg-[#e8edff] text-[#0033FF] dark:bg-[#0033FF]/15 dark:text-[#6688ff]"
                    : "text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-white/[0.04]"
                }`}
              >
                {active && (
                  <span className="w-1 h-5 bg-[#0033FF] rounded-full shrink-0" />
                )}
                {page.title}
              </Link>
            );
          })}
        </div>

        {/* Auth section */}
        <div className="px-4 pb-8 pt-4 border-t border-slate-100 dark:border-white/[0.06] shrink-0">
          {!router.pathname.includes("auth") && !isLoading && !currentUser && (
            <div className="flex flex-col gap-2.5">
              <button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  router.push({ pathname: "/auth", query: { mode: "login" } }, undefined, { shallow: true });
                }}
                className="w-full py-3.5 text-sm font-bold text-white bg-[#0033FF] hover:bg-[#0029d6] rounded-xl shadow-sm transition-all"
              >
                Մուտք
              </button>
              <button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  router.push({ pathname: "/auth", query: { mode: "registration" } }, undefined, { shallow: true });
                }}
                className="w-full py-3.5 text-sm font-bold text-[#0033FF] bg-[#e8edff] dark:bg-[#0033FF]/10 hover:bg-[#dbe3ff] dark:hover:bg-[#0033FF]/20 rounded-xl transition-all"
              >
                Գրանցվել
              </button>
            </div>
          )}

          {!isLoading && currentUser && (
            <div className="flex items-center justify-between gap-3 p-3 bg-slate-50 dark:bg-white/[0.04] rounded-xl border border-slate-100 dark:border-white/[0.06]">
              <button
                className="flex items-center gap-3 min-w-0"
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  router.push("/profile");
                }}
              >
                <Image
                  width={40}
                  height={40}
                  src={currentUser?.photoURL || "/defaultuserprofile.webp"}
                  className="object-cover rounded-full w-10 h-10 ring-2 ring-[#0033FF]/30 shrink-0"
                  alt="avatar"
                />
                <span className="text-sm font-semibold text-slate-900 dark:text-white truncate">
                  {currentUser.displayName || "Պրոֆիլ"}
                </span>
              </button>
              <button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  signOut();
                }}
                className="px-3 py-1.5 text-xs font-semibold text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-all shrink-0"
              >
                Դուրս գալ
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ResponsiveAppBar;

